import { useMemo, useState } from 'react';
import {
  Search,
  Phone,
  Mail,
  MapPin,
  AlertTriangle,
  Wrench,
  Building2,
  ChevronDown,
  FileText,
} from 'lucide-react';
import { formatDateTime } from '@/utils/dateUtils';
import { ISavV2 } from '@/models/savV2';
import { SavV2EventType } from '@/models/savV2Event';
import { SavLocationMap } from './SavLocationMap';

interface RepairerSearchStepProps {
  sav: ISavV2;
  virtualStatus?: string;
  completedAt?: string | null;
}

interface TimelineItem {
  id: string;
  date: string;
  repairerName: string;
  repairerUuid?: string;
  type: 'contact' | 'assigned' | 'unassigned' | 'contacted_event' | 'created';
  outcome?: 'available' | 'unavailable' | 'no_answer' | 'pending';
  notes?: string;
  contactedBy?: string;
  contactMethod?: string;
}

function formatSearchDuration(startDate?: string, endDate?: string): string {
  if (!startDate) return '—';
  const start = new Date(startDate);
  const end = endDate ? new Date(endDate) : new Date();
  const diffMs = end.getTime() - start.getTime();
  const hours = Math.floor(diffMs / 3600000);
  const minutes = Math.floor((diffMs % 3600000) / 60000);
  if (hours > 24) {
    const days = Math.floor(hours / 24);
    return `${days}j ${hours % 24}h`;
  }
  if (hours > 0) return `${hours}h ${minutes}min`;
  return `${minutes}min`;
}

export function RepairerSearchStep({ sav, completedAt }: RepairerSearchStepProps) {
  const [showPrevious, setShowPrevious] = useState(false);
  const intervention = sav.currentIntervention;
  const repairer = intervention?.repairer;
  const contacts = sav.repairerContacts ?? [];
  const repairerEvents = sav.repairerEvents ?? [];

  // Fusionner contacts + events en une timeline unifiée
  const timeline = useMemo<TimelineItem[]>(() => {
    const items: TimelineItem[] = [];

    // Ajouter les contacts de la table
    for (const c of contacts) {
      items.push({
        id: `contact-${c.id}`,
        date: c.createdAt,
        repairerName: c.repairerName,
        repairerUuid: c.repairerUuid,
        type: 'contact',
        outcome: c.outcome,
        notes: c.notes,
        contactedBy: c.contactedBy,
        contactMethod: c.contactMethod,
      });
    }

    // Ajouter les events (en évitant les doublons avec les contacts)
    const contactDates = new Set(contacts.map(c => `${c.repairerUuid}-${new Date(c.createdAt).getTime()}`));

    for (const evt of repairerEvents) {
      const m = evt.metadata;

      // Ignorer les événements sans réparateur identifiable
      if (!m?.repairerName && !m?.repairerUuid) continue;

      const evtKey = `${m?.repairerUuid}-${new Date(evt.createdAt).getTime()}`;

      // Éviter les doublons (même réparateur + même timestamp)
      if (evt.eventType === SavV2EventType.REPAIRER_CONTACTED && contactDates.has(evtKey)) continue;

      let type: TimelineItem['type'];
      switch (evt.eventType) {
        case SavV2EventType.REPAIRER_ASSIGNED: type = 'assigned'; break;
        case SavV2EventType.REPAIRER_UNASSIGNED: type = 'unassigned'; break;
        case SavV2EventType.REPAIRER_CONTACTED: type = 'contacted_event'; break;
        case SavV2EventType.REPAIRER_CREATED_FROM_GOOGLE_PLACE: continue;
        default: continue;
      }

      items.push({
        id: `event-${evt.id}`,
        date: evt.createdAt,
        repairerName: m?.repairerName || '—',
        repairerUuid: m?.repairerUuid,
        type,
        outcome: m?.outcome as any,
        notes: m?.notes,
        contactedBy: m?.contactedBy || evt.createdByName,
      });
    }

    return items.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [contacts, repairerEvents]);

  // Ancien réparateur chargé par l'API
  const prevRepairerData = sav.previousRepairer ?? null;
  const prevCompany = prevRepairerData?.company ?? null;

  // Séparer la timeline en 2 cycles si ancien réparateur détecté
  const { previousTimeline, currentTimeline } = useMemo(() => {
    if (!prevRepairerData?.invalidatedAt) return { previousTimeline: [], currentTimeline: timeline };
    const cutoff = new Date(prevRepairerData.invalidatedAt).getTime();
    return {
      previousTimeline: timeline.filter(t => new Date(t.date).getTime() < cutoff),
      currentTimeline: timeline.filter(t => new Date(t.date).getTime() >= cutoff),
    };
  }, [timeline, prevRepairerData?.invalidatedAt]);

  // Stats cycle courant
  const distinctRepairers = new Set(currentTimeline.map(t => t.repairerUuid || t.repairerName)).size;

  // Stats cycle précédent
  const prevDistinctRepairers = new Set(previousTimeline.map(t => t.repairerUuid || t.repairerName)).size;

  // Délai de recherche cycle courant
  const searchDuration = prevRepairerData?.invalidatedAt
    ? formatSearchDuration(prevRepairerData.invalidatedAt, intervention?.repairerAssignedAt)
    : intervention
      ? formatSearchDuration(intervention.createdAt, intervention.repairerAssignedAt)
      : '—';

  // Délai de recherche cycle précédent
  const prevSearchDuration = prevRepairerData
    ? formatSearchDuration(intervention?.createdAt, prevRepairerData.assignedAt)
    : '—';

  const [showContactedList, setShowContactedList] = useState(false);

  // Liste des réparateurs distincts avec date du premier contact
  const distinctRepairersList = useMemo(() => {
    const map = new Map<string, { name: string; date: string }>();
    for (let i = currentTimeline.length - 1; i >= 0; i--) {
      const t = currentTimeline[i];
      const key = t.repairerUuid || t.repairerName;
      map.set(key, { name: t.repairerName, date: t.date });
    }
    return Array.from(map.values());
  }, [currentTimeline]);

  const isRepairerFromQuote = (sav.hasQuote || !!sav.documentsData?.initialQuote) && !prevRepairerData && !!repairer;
  const isBeneficiaryAtRepairerWithoutQuote = !!sav.incidentInBusiness?.isInBusiness && !sav.hasQuote && !prevRepairerData;

  return (
    <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-shadow-sm tw-overflow-hidden">
      <div className="tw-border-b tw-border-gray-200">
        <h3 className="tw-flex tw-items-center tw-gap-2 tw-text-base tw-font-semibold tw-text-gray-900 tw-uppercase tw-px-4 tw-py-3 tw-m-0">
          <Search className="tw-w-4 tw-h-4 tw-text-affinicar-primary" />
          {prevCompany ? (
            <button
              type="button"
              onClick={() => setShowPrevious(false)}
              className={`tw-bg-transparent tw-border-none tw-p-0 tw-cursor-pointer tw-text-base tw-font-semibold tw-uppercase ${!showPrevious ? 'tw-text-gray-900' : 'tw-text-gray-400 hover:tw-text-gray-600'}`}
            >
              Recherche réparateur
            </button>
          ) : (
            <>Recherche réparateur</>
          )}
          {completedAt && !showPrevious && (
            <span className="tw-ml-auto tw-text-xs tw-font-normal tw-normal-case tw-text-gray-500">
              Terminée le {new Date(completedAt).toLocaleDateString('fr-FR')} à {new Date(completedAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
        </h3>
        {prevCompany && (
          <button
            type="button"
            onClick={() => setShowPrevious(true)}
            className={`tw-flex tw-items-center tw-gap-2 tw-px-4 tw-pb-3 tw-text-xs tw-bg-transparent tw-border-none tw-cursor-pointer ${showPrevious ? 'tw-text-orange-600 tw-font-medium' : 'tw-text-gray-400 hover:tw-text-orange-600'}`}
          >
            <AlertTriangle className="tw-w-3.5 tw-h-3.5" />
            Une première recherche de réparateur a été effectuée et invalidée
          </button>
        )}
      </div>
      {showPrevious && prevCompany ? (
        <div className="tw-p-4 sm:tw-p-6 tw-space-y-5 tw-bg-orange-50/30">

          {/* KPIs cycle précédent */}
          <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-3">
            <div className="tw-bg-white tw-rounded-lg tw-p-3 tw-text-center">
              <p className="tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-0">{prevDistinctRepairers}</p>
              <p className="tw-text-xs tw-text-gray-500 tw-mb-0">Réparateur(s) contacté(s)</p>
            </div>
            <div className="tw-bg-white tw-rounded-lg tw-p-3 tw-text-center">
              <p className="tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-0">{prevSearchDuration}</p>
              <p className="tw-text-xs tw-text-gray-500 tw-mb-0">Délai de recherche</p>
            </div>
          </div>

          {/* Réparateur assigné (invalidé) */}
          <div>
            <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
              <Wrench className="tw-w-4 tw-h-4 tw-text-gray-400" />
              <span className="tw-text-xs tw-text-gray-500 tw-uppercase tw-font-medium">Réparateur assigné (invalidé)</span>
              {prevRepairerData?.assignedAt && (
                <span className="tw-text-xs tw-text-gray-400">— assigné le {formatDateTime(prevRepairerData.assignedAt)}</span>
              )}
              {prevRepairerData?.invalidatedAt && (
                <span className="tw-text-xs tw-text-red-400">— invalidé le {formatDateTime(prevRepairerData.invalidatedAt)}</span>
              )}
            </div>
            <div className="tw-bg-white tw-rounded-lg tw-p-4 tw-space-y-2">
              <div className="tw-flex tw-items-center tw-gap-2">
                <Building2 className="tw-w-4 tw-h-4 tw-text-affinicar-primary" />
                <span className="tw-text-sm tw-font-semibold tw-text-gray-900">{prevCompany.name}</span>
              </div>
              {(prevCompany.address || prevCompany.city) && (
                <div className="tw-flex tw-items-start tw-gap-2">
                  <MapPin className="tw-w-3.5 tw-h-3.5 tw-text-gray-400 tw-mt-0.5" />
                  <span className="tw-text-sm tw-text-gray-600">
                    {[prevCompany.address, prevCompany.cp, prevCompany.city].filter(Boolean).join(', ')}
                  </span>
                </div>
              )}
              {prevCompany.phone && (
                <div className="tw-flex tw-items-center tw-gap-2">
                  <Phone className="tw-w-3.5 tw-h-3.5 tw-text-gray-400" />
                  <span className="tw-text-sm tw-text-gray-600">{prevCompany.phone}</span>
                </div>
              )}
              {prevCompany.email && (
                <div className="tw-flex tw-items-center tw-gap-2">
                  <Mail className="tw-w-3.5 tw-h-3.5 tw-text-gray-400" />
                  <span className="tw-text-sm tw-text-gray-600">{prevCompany.email}</span>
                </div>
              )}
            </div>
          </div>

          {/* Carte cycle précédent */}
          {prevCompany.lat && prevCompany.lng && sav.incidentLatitude && sav.incidentLongitude && (
            <SavLocationMap
              incidentLat={sav.incidentLatitude}
              incidentLng={sav.incidentLongitude}
              garantLat={prevCompany.lat}
              garantLng={prevCompany.lng}
              garantName={prevCompany.name}
              height="200px"
              distanceLabelPrefix="Le garage sélectionné se situe"
              distanceLabelSuffix="du lieu de la panne"
            />
          )}

        </div>
      ) : (
        <div className="tw-p-4 sm:tw-p-6 tw-space-y-5">

          {/* KPIs recherche ou badge devis client / bénéficiaire en garage */}
          {isRepairerFromQuote ? (
            <div className="tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded-lg tw-p-3 tw-flex tw-items-center tw-gap-2">
              <FileText className="tw-w-4 tw-h-4 tw-text-blue-600 tw-flex-shrink-0" />
              <span className="tw-text-sm tw-font-medium tw-text-blue-800">Réparateur issu du devis fourni par le client</span>
            </div>
          ) : isBeneficiaryAtRepairerWithoutQuote ? (
            <div className="tw-bg-amber-50 tw-border tw-border-amber-200 tw-rounded-lg tw-p-3 tw-flex tw-items-center tw-gap-2">
              <Building2 className="tw-w-4 tw-h-4 tw-text-amber-600 tw-flex-shrink-0" />
              <span className="tw-text-sm tw-font-medium tw-text-amber-800">Bénéficiaire déjà chez un réparateur — devis en attente</span>
            </div>
          ) : (
            <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-3">
              <div className="tw-bg-gray-50 tw-rounded-lg tw-p-3 tw-text-center">
                <p className="tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-0">{distinctRepairers}</p>
                {distinctRepairers > 1 ? (
                  <button
                    type="button"
                    onClick={() => setShowContactedList(!showContactedList)}
                    className="tw-text-xs tw-text-gray-500 tw-mb-0 tw-bg-transparent tw-border-none tw-cursor-pointer tw-inline-flex tw-items-center tw-gap-1 tw-p-0 hover:tw-text-affinicar-primary"
                  >
                    Réparateur(s) contacté(s)
                    <ChevronDown className={`tw-w-3 tw-h-3 tw-transition-transform ${showContactedList ? 'tw-rotate-180' : ''}`} />
                  </button>
                ) : (
                  <p className="tw-text-xs tw-text-gray-500 tw-mb-0">Réparateur(s) contacté(s)</p>
                )}
                {showContactedList && (
                  <div className="tw-mt-2 tw-text-left tw-space-y-1.5">
                    {distinctRepairersList.map((r, i) => (
                      <div key={i} className="tw-flex tw-items-center tw-justify-between tw-bg-white tw-rounded-lg tw-px-3 tw-py-1.5 tw-text-xs">
                        <span className="tw-font-medium tw-text-gray-900">{r.name}</span>
                        <span className="tw-text-gray-500">{formatDateTime(r.date)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="tw-bg-gray-50 tw-rounded-lg tw-p-3 tw-text-center">
                <p className="tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-0">{searchDuration}</p>
                <p className="tw-text-xs tw-text-gray-500 tw-mb-0">Délai de recherche</p>
              </div>
            </div>
          )}

          {/* Détails du réparateur assigné */}
          {repairer && (
            <div>
              <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
                <Wrench className="tw-w-4 tw-h-4 tw-text-gray-400" />
                <span className="tw-text-xs tw-text-gray-500 tw-uppercase tw-font-medium">Réparateur assigné</span>
                {intervention?.repairerAssignedAt && (
                  <span className="tw-text-xs tw-text-gray-400">— {formatDateTime(intervention.repairerAssignedAt)}</span>
                )}
              </div>
              <div className="tw-bg-gray-50 tw-rounded-lg tw-p-4 tw-space-y-2">
                <div className="tw-flex tw-items-center tw-gap-2">
                  <Building2 className="tw-w-4 tw-h-4 tw-text-affinicar-primary" />
                  <span className="tw-text-sm tw-font-semibold tw-text-gray-900">{repairer.name}</span>
                </div>
                {(repairer.address || repairer.city) && (
                  <div className="tw-flex tw-items-start tw-gap-2">
                    <MapPin className="tw-w-3.5 tw-h-3.5 tw-text-gray-400 tw-mt-0.5" />
                    <span className="tw-text-sm tw-text-gray-600">
                      {[repairer.address, repairer.cp, repairer.city].filter(Boolean).join(', ')}
                    </span>
                  </div>
                )}
                {repairer.phone && (
                  <div className="tw-flex tw-items-center tw-gap-2">
                    <Phone className="tw-w-3.5 tw-h-3.5 tw-text-gray-400" />
                    <a href={`tel:${repairer.phone}`} className="tw-text-sm tw-text-affinicar-primary hover:tw-underline tw-no-underline">{repairer.phone}</a>
                  </div>
                )}
                {repairer.email && (
                  <div className="tw-flex tw-items-center tw-gap-2">
                    <Mail className="tw-w-3.5 tw-h-3.5 tw-text-gray-400" />
                    <a href={`mailto:${repairer.email}`} className="tw-text-sm tw-text-affinicar-primary hover:tw-underline tw-no-underline">{repairer.email}</a>
                  </div>
                )}
              </div>
            </div>
          )}

          {!repairer && !intervention?.repairerUuid && (
            <div className="tw-bg-orange-50 tw-border tw-border-orange-200 tw-rounded-lg tw-p-4 tw-text-center">
              <Search className="tw-w-6 tw-h-6 tw-text-orange-500 tw-mx-auto tw-mb-2" />
              <p className="tw-text-sm tw-font-medium tw-text-orange-800 tw-mb-0">Recherche de réparateur en cours</p>
            </div>
          )}

          {/* Carte réparateur */}
          {repairer && sav.incidentLatitude && sav.incidentLongitude && (
            <SavLocationMap
              incidentLat={sav.incidentLatitude}
              incidentLng={sav.incidentLongitude}
              garantLat={repairer.lat}
              garantLng={repairer.lng}
              garantName={repairer.name}
              height="200px"
              distanceLabelPrefix="Le garage sélectionné se situe"
              distanceLabelSuffix="du lieu de la panne"
            />
          )}

        </div>
      )}
    </div>
  );
}

export default RepairerSearchStep;
